import { useState, useEffect, useContext } from "react"
import { AuthContext } from "../context/AuthContext"
import axios from "axios"
import { Table, Tag, Button, Segmented } from "antd"
import { ArrowLeftOutlined } from "@ant-design/icons"
import NocForm from "../components/NocForm"

export default function ListNoc() {
    const [data, setData] = useState([])
    const [loading, setLoading] = useState(false)
    const [selectedRecord, setSelectedRecord] = useState(null)
    const { authData, backUrl } = useContext(AuthContext)
    const [approvalFilter, setApprovalFilter] = useState("PENDING")


    const fetchData = async () => {
        setLoading(true)
        try {
            const res = await axios.get(`${backUrl}/api/clearance/requests`, {
                headers: {
                    Authorization: `Bearer ${authData?.access}`
                }
            })
            setData(res.data.results)
            console.log("Data : ", res.data.results)
        } catch (error) {
            console.error("Error fetching data:", error)
        } finally {
            setLoading(false)
        }
    }

    useEffect(() => {
        fetchData()
    }, [])

    const getStatusColor = (status) => {
        const colors = {
            DRAFT: "default",
            PENDING: "processing",
            APPROVED: "success",
            REJECTED: "error"
        }
        return colors[status] || "default"
    }

    const handleViewDetail = (record) => {
        setSelectedRecord(record)
        console.log("Record :", record)
    }

    const isApprovedForRole = (record) => {
        if (authData?.employee?.is_superuser) {
            return record.is_final_approved
        }

        switch (authData?.employee?.department) {
            case 25: // HR
                return record.hr_section?.is_approved
            case 12: // IT
                return record.it_section?.is_approved
            case 14: // Finance
                return record.finance_section?.is_approved
            default:
                return false
        }
    }

    const filteredData = data.filter((record) => {
        const approved = isApprovedForRole(record)

        if (approvalFilter === "APPROVED") {
            return approved === true
        }

        // Default → Pending
        return approved !== true
    })



    const handleBack = () => {
        setSelectedRecord(null)
        fetchData() // Refresh data when going back
    }

    const transformRecordForNocForm = (record) => {
        return {
            ...record,
            first_name: record.emp_no, // or fetch actual name if available
            emp_code: record.emp_no,
            designation_name: record.designation,
            department_name: record.department,
            date_of_joining: record.joining_date,
            date_of_leaving: record.resignation_date,
            last_working_day: record.relieving_date,
            organisation_name: 'Baashyaam Constructions Pvt Ltd', // Set default or from data
            id: record.employee,
            emp_id: record.employee
        };
    };

    // If a record is selected, show NocForm
    if (selectedRecord) {
        return (
            <div style={{ padding: "24px" }}>
                <Button
                    type="primary"
                    icon={<ArrowLeftOutlined />}
                    onClick={handleBack}
                    style={{ borderBottom: "2px solid #0e497b", background: 'transparent', color: 'black' }}
                >
                    Back to List
                </Button>
                <NocForm employee={transformRecordForNocForm(selectedRecord)} requestId={selectedRecord.id} />
            </div>
        )
    }

    const columns = [
        {
            title: "Employee No",
            dataIndex: "emp_no",
            key: "emp_no",
            width: 120,
            render: (_, record) => (
                <Button
                    type="link"
                    onClick={() => handleViewDetail(record)}
                    style={{ padding: 0, fontWeight: "bold" }}
                >
                    {record.emp_no}
                </Button>
            )
        },
        {
            title: "Department",
            dataIndex: "department",
            key: "department",
            width: 150
        },
        {
            title: "Designation",
            dataIndex: "designation",
            key: "designation",
            width: 180
        },
        {
            title: "Joining Date",
            dataIndex: "joining_date",
            key: "joining_date",
            width: 120,
            render: (date) => date ? new Date(date).toLocaleDateString() : "-"
        },
        {
            title: "Resignation Date",
            dataIndex: "resignation_date",
            key: "resignation_date",
            width: 140,
            render: (date) => date ? new Date(date).toLocaleDateString() : "-"
        },
        {
            title: "Relieving Date",
            dataIndex: "relieving_date",
            key: "relieving_date",
            width: 130,
            render: (date) => date ? new Date(date).toLocaleDateString() : "-"
        },
        authData?.employee?.is_superuser && {
            title: "Status",
            dataIndex: "status",
            key: "status",
            width: 100,
            render: (status) => (
                <Tag color={getStatusColor(status)}>{status}</Tag>
            )
        },
        // HR
        (authData?.employee?.department === 25 || authData?.employee?.is_superuser) && {
            title: "HR Approved",
            key: "hr_approved",
            width: 120,
            render: (_, record) => (
                <Tag color={record.hr_section?.is_approved ? "success" : "default"}>
                    {record.hr_section?.is_approved ? "Yes" : "No"}
                </Tag>
            )
        },

        // IT
        (authData?.employee?.department === 12 || authData?.employee?.is_superuser) && {
            title: "IT Approved",
            key: "it_approved",
            width: 120,
            render: (_, record) => (
                <Tag color={record.it_section?.is_approved ? "success" : "default"}>
                    {record.it_section?.is_approved ? "Yes" : "No"}
                </Tag>
            )
        },

        // Finance
        (authData?.employee?.department === 14 || authData?.employee?.is_superuser) && {
            title: "Finance Approved",
            key: "finance_approved",
            width: 140,
            render: (_, record) => (
                <Tag color={record.finance_section?.is_approved ? "success" : "default"}>
                    {record.finance_section?.is_approved ? "Yes" : "No"}
                </Tag>
            )
        },

        // Superuser
        authData?.employee?.is_superuser && {
            title: "Final Approved",
            dataIndex: "is_final_approved",
            key: "is_final_approved",
            width: 130,
            render: (approved) => (
                <Tag color={approved ? "success" : "default"}>
                    {approved ? "Yes" : "No"}
                </Tag>
            )
        }
    ].filter(Boolean); // 🔥 VERY IMPORTANT


    // Show table when no record is selected
    return (
        <div style={{ padding: "24px" }}>
            <Segmented
                options={[
                    { label: "Pending", value: "PENDING" },
                    { label: "Approved", value: "APPROVED" }
                ]}
                value={approvalFilter}
                onChange={setApprovalFilter}
                style={{ marginBottom: 16 }}
            />

            <Table
                columns={columns}
                dataSource={data}
                rowKey="id"
                loading={loading}
                scroll={{ x: 1500 }}
                pagination={{
                    pageSize: 10,
                    showSizeChanger: true,
                    showTotal: (total) => `Total ${total} records`
                }}
                components={{
                    header: {
                        cell: (props) => (
                            <th {...props} style={{ backgroundColor: "#0e497b", color: 'white', marginTop: '20px' }}>
                                {props.children}
                            </th>
                        ),
                    },
                }}
                bordered
            />
        </div>
    )
}